'use strict';

function isArray(obj) {
    return Array.isArray(obj);
}
function isString(obj) {
    return typeof obj === 'string';
}
function isObject(obj) {
    return !!obj && typeof obj === 'object' && !isArray(obj);
}
function isFunction(obj) {
    return typeof obj === 'function';
}
function isPrimitive(arg) {
    const type = typeof arg;
    return arg !== undefined && type !== 'object' && type !== 'function';
}
function isSymbol(obj) {
    return typeof obj === 'symbol';
}
function isBoolean(obj) {
    return typeof obj === 'boolean';
}
function isPromise(obj) {
    return obj instanceof Promise;
}
function isEmpty(obj) {
    return obj && Object.keys(obj).length === 0;
}
const setPrimitives = new Set(['boolean', 'string', 'number']);
/** @internal */
function isActualPrimitive(arg) {
    return setPrimitives.has(typeof arg);
}
/** @internal */
function isChildNodeValue(node) {
    return !!node.parent;
}

let trackCount = 0;
const trackingQueue = [];
const tracking = {
    current: undefined,
    inRemoteChange: false,
};
function beginTracking() {
    // Keep a copy of the previous tracking context so it can be restored
    // when this context is complete
    trackingQueue.push(tracking.current);
    trackCount++;
    tracking.current = {};
}
function endTracking() {
    // Restore the previous tracking context
    trackCount--;
    if (trackCount < 0) {
        trackCount = 0;
        if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
            // Shouldn't be possible, but leave as a sanity check
            debugger;
        }
    }
    tracking.current = trackingQueue.pop();
}
function updateTracking(node, track) {
    if (trackCount) {
        const tracker = tracking.current;
        if (tracker) {
            if (!tracker.nodes) {
                tracker.nodes = new Map();
            }
            const existing = tracker.nodes.get(node.id);
            if (existing) {
                existing.track = existing.track || track;
                existing.num++;
            }
            else {
                tracker.nodes.set(node.id, { node, track, num: 1 });
            }
        }
    }
}

// If changing dateModifiedKey, change '@' in indexeddb-preloader
const dateModifiedKey = '@';
const symbolDateModified = /* @__PURE__ */ Symbol('dateModified');
const symbolIsObservable = Symbol('isObservable');
const symbolIsEvent = Symbol('isEvent');
const symbolGetNode = Symbol('getNode');
const symbolUndef = Symbol('undef');
const symbolDelete = /* @__PURE__ */ Symbol('delete');
const symbolOpaque = Symbol('opaque');
const extraPrimitiveActivators = new Map([
    ['$$typeof', true],
    [Symbol.toPrimitive, true],
]);
const extraPrimitiveProps = new Map();
const nextNodeID = { current: 0 };
function get(node, track) {
    // Track by default
    updateTracking(node, track);
    return peek(node);
}
function peek(node) {
    const root = node.root;
    if (root.activate) {
        root.activate();
        root.activate = undefined;
    }
    return getNodeValue(node);
}
function getNodeValue(node) {
    const arr = [];
    let n = node;
    while (isChildNodeValue(n)) {
        arr.push(n.key);
        n = n.parent;
    }
    let child = node.root._;
    for (let i = arr.length - 1; i >= 0; i--) {
        if (arr[i] !== undefined && child) {
            child = child[arr[i]];
        }
    }
    return child;
}
function getChildNode(node, key) {
    var _a;
    if (isString(key)) {
        const n = +key;
        // Convert to number if it's a string representing a valid number
        if (n - n < 1)
            key = n;
    }
    // Get the child by id if included, or by key
    let child = (_a = node.children) === null || _a === void 0 ? void 0 : _a.get(key);
    // Create the child node if it doesn't already exist
    if (!child) {
        child = {
            id: nextNodeID.current++,
            root: node.root,
            parent: node,
            key,
            // id,
        };
        if (!node.children) {
            node.children = new Map();
        }
        node.children.set(key, child);
    }
    return child;
}
function ensureNodeValue(node) {
    let value = getNodeValue(node);
    if (!value) {
        if (isChildNodeValue(node)) {
            const parent = ensureNodeValue(node.parent);
            value = parent[node.key] = {};
        }
        else {
            value = node.root._ = {};
        }
    }
    return value;
}
function findIDKey(obj) {
    return isObject(obj) ? ('id' in obj ? 'id' : '_id' in obj ? '_id' : '__id' in obj ? '__id' : undefined) : undefined;
}

function isObservable(obs) {
    return obs && !!obs[symbolIsObservable];
}
function computeSelector(selector, e) {
    let c = selector;
    if (isFunction(c)) {
        c = e ? c(e) : c();
    }
    return isObservable(c) ? c.get() : c;
}
function getNode(obs) {
    return obs[symbolGetNode];
}
function getObservableIndex(obs) {
    const node = getNode(obs);
    const n = +node.key;
    return n - n < 1 ? +n : -1;
}
function opaqueObject(value) {
    value[symbolOpaque] = true;
    return value;
}
function lockObservable(obs, value) {
    var _a;
    const root = (_a = getNode(obs)) === null || _a === void 0 ? void 0 : _a.root;
    if (root) {
        root.locked = value;
    }
}
function mergeIntoObservable(target, ...sources) {
    var _a, _b;
    if (!sources.length)
        return target;
    const source = sources.shift();
    const needsSet = isObservable(target);
    const targetValue = needsSet ? target.peek() : target;
    const isTargetObj = isObject(targetValue);
    const isTargetArr = isArray(targetValue);
    if ((isTargetObj && isObject(source)) || (isTargetArr && isArray(source))) {
        const keys = isTargetArr ? source : Object.keys(source);
        if (source[symbolDateModified]) {
            keys.push(symbolDateModified);
        }
        for (let i = 0; i < keys.length; i++) {
            const key = isTargetArr ? i : keys[i];
            const sourceValue = source[key];
            if (isObject(sourceValue)) {
                if (!needsSet && (!targetValue[key] || !isObject(targetValue[key]))) {
                    target[key] = {};
                }
                mergeIntoObservable(target[key], sourceValue);
            }
            else if (sourceValue === symbolDelete) {
                needsSet && ((_a = target[key]) === null || _a === void 0 ? void 0 : _a.delete) ? target[key].delete() : delete target[key];
            }
            else {
                needsSet && ((_b = target[key]) === null || _b === void 0 ? void 0 : _b.set)
                    ? target[key].set(sourceValue)
                    : (target[key] = sourceValue);
            }
        }
    }
    else if (needsSet && !(isTargetObj && source === undefined)) {
        target.set(source);
    }
    return mergeIntoObservable(target, ...sources);
}
function constructObject(path, value) {
    let out;
    if (path.length > 0) {
        let o = (out = {});
        for (let i = 0; i < path.length; i++) {
            const p = path[i];
            o[p] = i === path.length - 1 ? value : {};
            o = o[p];
        }
    }
    else {
        out = value;
    }
    return out;
}
function deconstructObject(path, value) {
    let o = value;
    for (let i = 0; i < path.length; i++) {
        const p = path[i];
        o = o[p];
    }
    return o;
}
function clone(obj) {
    return JSON.parse(JSON.stringify(obj));
}

let timeout;
let numInBatch = 0;
let _batch = [];
// Use a Map of callbacks for fast lookups to update the BatchItem
let _batchMap = new Map();
function onActionTimeout() {
    if (_batch.length > 0) {
        if (process.env.NODE_ENV === 'development') {
            console.error('Forcibly completing observableBatcher because end() was never called. This may be due to an uncaught error between begin() and end().');
        }
        endBatch(/*force*/ true);
    }
}
function batchNotify(b) {
    const isFunc = isFunction(b);
    const cb = isFunc ? b : b.cb;
    if (numInBatch > 0) {
        const existing = _batchMap.get(cb);
        const it = isFunc ? true : b;
        // If this callback already exists, make sure it has the latest value but do not add it
        if (existing) {
            if (!isFunc) {
                existing.value = b.value;
                existing.changes.push(...b.changes);
            }
        }
        else {
            _batch.push(b);
            _batchMap.set(cb, it);
        }
    }
    else {
        isFunc ? b() : b.cb(b.value, b.getPrevious, b.changes, b.node);
    }
}
function batch(fn) {
    beginBatch();
    fn();
    endBatch();
}
function beginBatch() {
    numInBatch++;
    // Set a timeout to call end() in case end() is never called or there's an uncaught error
    if (!timeout) {
        timeout = setTimeout(onActionTimeout, 0);
    }
}
function endBatch(force) {
    numInBatch--;
    if (numInBatch <= 0 || force) {
        clearTimeout(timeout);
        timeout = undefined;
        numInBatch = 0;
        // Save batch locally and reset _batch first because a new batch could begin while looping over callbacks.
        // This can happen with observableComputed for example.
        const batch = _batch;
        _batch = [];
        _batchMap = new Map();
        for (let i = 0; i < batch.length; i++) {
            const b = batch[i];
            if (isFunction(b)) {
                b();
            }
            else {
                const { cb, value, getPrevious: prev, changes, node } = b;
                cb(value, prev, changes, node);
            }
        }
    }
}

function createPreviousHandler(value, path, prevAtPath) {
    // Create a function that clones the current state and injects the previous data at the changed path
    return function () {
        let clone = value ? JSON.parse(JSON.stringify(value)) : path.length > 0 ? {} : value;
        let o = clone;
        if (path.length > 0) {
            let i;
            for (i = 0; i < path.length - 1; i++) {
                o = o[path[i]];
            }
            o[path[i]] = prevAtPath;
        }
        else {
            clone = prevAtPath;
        }
        return clone;
    };
}
function doNotify(node, value, path, valueAtPath, prevAtPath, level, whenOptimizedOnlyIf) {
    const listeners = node.listeners;
    if (listeners) {
        let getPrevious;
        let arr = Array.from(listeners);
        for (let i = 0; i < arr.length; i++) {
            const listenerFn = arr[i];
            const { track, noArgs } = listenerFn;
            const ok = track === true ? level <= 0 : track === 'optimize' ? whenOptimizedOnlyIf && level <= 0 : true;
            // Notify if listener is not shallow or if this is the first level
            if (ok) {
                // Create a function to get the previous data. Computing a clone of previous data can be expensive if doing
                // it often, so leave it up to the caller.
                if (!noArgs && !getPrevious) {
                    getPrevious = createPreviousHandler(value, path, prevAtPath);
                }
                batchNotify(noArgs
                    ? listenerFn.listener
                    : {
                        cb: listenerFn.listener,
                        value,
                        getPrevious,
                        changes: [
                            {
                                path,
                                valueAtPath,
                                prevAtPath,
                            },
                        ],
                        node,
                    });
            }
        }
    }
}
function _notifyParents(node, value, path, valueAtPath, prevAtPath, level, whenOptimizedOnlyIf) {
    // Do the notify
    doNotify(node, value, path, valueAtPath, prevAtPath, level, whenOptimizedOnlyIf);
    // If not root notify up through parents
    if (node.parent) {
        const parent = node.parent;
        if (parent) {
            const parentValue = getNodeValue(parent);
            _notifyParents(parent, parentValue, [node.key].concat(path), valueAtPath, prevAtPath, level + 1, whenOptimizedOnlyIf);
        }
    }
}
function notify(node, value, prev, level, whenOptimizedOnlyIf) {
    // Notify self and up through parents
    _notifyParents(node, value, [], value, prev, level, whenOptimizedOnlyIf);
}

function onChange(node, callback, track, noArgs) {
    let listeners = node.listeners;
    if (!listeners) {
        node.listeners = listeners = new Set();
    }
    const listener = { listener: callback, track: track, noArgs };
    listeners.add(listener);
    return () => listeners.delete(listener);
}

function ObservablePrimitiveClass(node) {
    this._node = node;
    this.set = this.set.bind(this);
    this.toggle = this.toggle.bind(this);
}
// Getters
Object.defineProperty(ObservablePrimitiveClass.prototype, symbolGetNode, {
    configurable: true,
    get() {
        return this._node;
    },
});
Object.defineProperty(ObservablePrimitiveClass.prototype, symbolIsObservable, {
    configurable: true,
    value: true,
});
Object.defineProperty(ObservablePrimitiveClass.prototype, symbolIsEvent, {
    configurable: true,
    value: false,
});
ObservablePrimitiveClass.prototype.peek = function () {
    const root = this._node.root;
    if (root.activate) {
        root.activate();
        root.activate = undefined;
    }
    return root._;
};
ObservablePrimitiveClass.prototype.get = function () {
    const node = this._node;
    updateTracking(node);
    return this.peek();
};
// Setters
ObservablePrimitiveClass.prototype.set = function (value) {
    if (isFunction(value)) {
        value = value(this._node.root._);
    }
    if (this._node.root.locked) {
        throw new Error(process.env.NODE_ENV === 'development'
            ? '[legend-state] Cannot modify an observable while it is locked. Please make sure that you unlock the observable before making changes.'
            : '[legend-state] Modified locked observable');
    }
    const root = this._node.root;
    const prev = root._;
    root._ = value;
    doNotify(this._node, value, [], value, prev, 0);
    return this;
};
ObservablePrimitiveClass.prototype.toggle = function () {
    const value = this.peek();
    if (isBoolean(value)) {
        this.set(!value);
    }
    else if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
        throw new Error('[legend-state] Cannot toggle a non-boolean value');
    }
    return !value;
};
// Listener
ObservablePrimitiveClass.prototype.onChange = function (cb, track, noArgs) {
    return onChange(this._node, cb, track, noArgs);
};

let inSet = false;
let inAssign = false;
const ArrayModifiers = new Set([
    'copyWithin',
    'fill',
    'from',
    'pop',
    'push',
    'reverse',
    'shift',
    'sort',
    'splice',
    'unshift',
]);
const ArrayLoopers = new Set(['every', 'some', 'filter', 'forEach', 'map', 'join']);
const NotifySpecifically = new Set([symbolDateModified]);
const objectFns = new Map([
    ['get', get],
    ['set', set],
    ['peek', peek],
    ['onChange', onChange],
    ['assign', assign],
    ['delete', deleteFn],
    ['toggle', toggle],
]);
function collectionSetter(node, target, prop, ...args) {
    var _a;
    const prevValue = (isArray(target) && target.slice()) || target;
    const ret = target[prop].apply(target, args);
    if (node) {
        const hasParent = isChildNodeValue(node);
        const key = hasParent ? node.key : '_';
        const parentValue = hasParent ? getNodeValue(node.parent) : node.root;
        // Set the object to the previous value first
        parentValue[key] = prevValue;
        // Then set with the new value so it notifies with the correct prevValue
        setKey((_a = node.parent) !== null && _a !== void 0 ? _a : node, hasParent ? key : symbolUndef, target);
    }
    // Return the original value
    return ret;
}
function updateNodes(parent, obj, prevValue) {
    if (isObject(obj) && obj[symbolOpaque]) {
        const isDiff = obj !== prevValue;
        if (isDiff) {
            if (parent.listeners) {
                doNotify(parent, obj, [], obj, prevValue, 0);
            }
        }
        return isDiff;
    }
    const isArr = isArray(obj);
    let prevChildrenById;
    let moved;
    // If array it's faster to just use the array
    const keys = isArr ? obj : obj ? Object.keys(obj) : [];
    let idField;
    let hasADiff = false;
    if (isArr && isArray(prevValue)) {
        // Construct a map of previous indices for computing move
        if (prevValue.length > 0) {
            const firstPrevValue = prevValue[0];
            if (firstPrevValue) {
                idField = findIDKey(firstPrevValue);
                if (idField) {
                    prevChildrenById = new Map();
                    moved = [];
                    if (parent.children) {
                        for (let i = 0; i < prevValue.length; i++) {
                            const p = prevValue[i];
                            if (p) {
                                const child = parent.children.get(i);
                                if (child) {
                                    prevChildrenById.set(p[idField], child);
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    else if (prevValue && (!obj || obj.hasOwnProperty)) {
        // For keys that have been removed from object, notify and update children recursively
        const keysPrev = Object.keys(prevValue);
        const lengthPrev = keysPrev.length;
        for (let i = 0; i < lengthPrev; i++) {
            const key = keysPrev[i];
            if (!keys.includes(key)) {
                hasADiff = true;
                let child = getChildNode(parent, key);
                const prev = prevValue[key];
                if (!isPrimitive(prev)) {
                    updateNodes(child, undefined, prev);
                }
                if (child.listeners) {
                    doNotify(child, undefined, [], undefined, prev, 0);
                }
            }
        }
    }
    if (obj && !isPrimitive(obj)) {
        const length = keys.length;
        hasADiff = hasADiff || (obj === null || obj === void 0 ? void 0 : obj.length) !== (prevValue === null || prevValue === void 0 ? void 0 : prevValue.length);
        const isArrDiff = hasADiff;
        let didMove = false;
        for (let i = 0; i < length; i++) {
            const key = isArr ? i : keys[i];
            const value = obj[key];
            const prev = prevValue === null || prevValue === void 0 ? void 0 : prevValue[key];
            let isDiff = value !== prev;
            if (isDiff) {
                const id = value === null || value === void 0 ? void 0 : value[idField];
                let child = getChildNode(parent, key);
                // Detect moves within an array. Need to move the original proxy to the new position to keep
                // the proxy stable, so that listeners to this node will be unaffected by the array shift.
                if (isArr && id !== undefined) {
                    // Find the previous position of this element in the array
                    const prevChild = id !== undefined ? prevChildrenById === null || prevChildrenById === void 0 ? void 0 : prevChildrenById.get(id) : undefined;
                    if (!prevChild) {
                        // This id was not in the array before so it does not need to notify children
                        isDiff = false;
                        hasADiff = true;
                    }
                    else if (prevChild !== undefined && prevChild.key !== key) {
                        // If array length changed then move the original node to the current position.
                        // That should be faster than notifying every single element that
                        // it's in a new position.
                        if (isArrDiff) {
                            child = prevChild;
                            parent.children.delete(child.key);
                            child.key = key;
                            moved.push([key, child]);
                        }
                        didMove = true;
                        // And check for diff against the previous value in the previous position
                        const prevOfNode = prevChild;
                        isDiff = prevOfNode !== value;
                    }
                }
                if (isDiff) {
                    // Array has a new / modified element
                    hasADiff = true;
                    // If object iterate through its children
                    if (!isPrimitive(value)) {
                        updateNodes(child, value, prev);
                    }
                }
                if (isDiff || !isArrDiff) {
                    // Notify for this child if this element is different and it has listeners
                    // Or if the position changed in an array whose length did not change
                    // But do not notify child if the parent is an array with changing length -
                    // the array's listener will cover it
                    if (child.listeners) {
                        doNotify(child, value, [], value, prev, 0, !isArrDiff);
                    }
                }
            }
        }
        if (moved) {
            for (let i = 0; i < moved.length; i++) {
                const [key, child] = moved[i];
                parent.children.set(key, child);
            }
        }
        // The full array does not need to re-render if the length is the same
        // So don't notify shallow listeners
        return hasADiff || didMove;
    }
    else if (prevValue) {
        // If value got set to undefined, it has a diff
        return true;
    }
    return false;
}
function getProxy(node, p) {
    // Get the child node if p prop
    if (p !== undefined)
        node = getChildNode(node, p);
    // Create a proxy if not already cached and return it
    return node.proxy || (node.proxy = new Proxy(node, proxyHandler));
}
const proxyHandler = {
    get(node, p) {
        var _a, _b;
        // Return true is called by isObservable()
        if (p === symbolIsObservable) {
            return true;
        }
        if (p === symbolIsEvent) {
            return false;
        }
        if (p === symbolGetNode) {
            return node;
        }
        const fn = objectFns.get(p);
        // If this is an observable function, call it
        if (fn) {
            return function (a, b, c) {
                const l = arguments.length;
                // Array call and apply are slow so micro-optimize this hot path.
                // The observable functions depends on the number of arguments so we have to
                // call it with the correct arguments, not just undefined
                return l > 2 ? fn(node, a, b, c) : l > 1 ? fn(node, a, b) : fn(node, a);
            };
        }
        let value = getNodeValue(node);
        const isValuePrimitive = isPrimitive(value);
        if (value === undefined || value === null || isValuePrimitive) {
            if (extraPrimitiveProps.size && (node.isActivatedPrimitive || extraPrimitiveActivators.has(p))) {
                node.isActivatedPrimitive = true;
                const vPrim = extraPrimitiveProps.get(p);
                if (vPrim !== undefined) {
                    return (_b = (_a = vPrim === null || vPrim === void 0 ? void 0 : vPrim.__fn) === null || _a === void 0 ? void 0 : _a.call(vPrim, getProxy(node))) !== null && _b !== void 0 ? _b : vPrim;
                }
            }
        }
        const vProp = value === null || value === void 0 ? void 0 : value[p];
        if (isObject(value) && value[symbolOpaque]) {
            return vProp;
        }
        // Handle function calls
        if (isFunction(vProp)) {
            if (isArray(value)) {
                if (ArrayModifiers.has(p)) {
                    // Call the wrapped modifier function
                    return (...args) => collectionSetter(node, value, p, ...args);
                }
                else if (ArrayLoopers.has(p)) {
                    // Update that this node was accessed for observers
                    // Listen to the array shallowly
                    updateTracking(node, true);
                    return function (cbOrig, thisArg) {
                        function cb(_, index, array) {
                            return cbOrig(getProxy(node, index), index, array);
                        }
                        return value[p](cb, thisArg);
                    };
                }
            }
            // Return the function bound to the value
            return vProp.bind(value);
        }
        // Accessing primitive returns the raw value
        if (isPrimitive(vProp)) {
            // Update that this primitive node was accessed for observers
            if (isArray(value) && p === 'length') {
                updateTracking(node, true);
                // } else if (!isPrimitive(value)) {
                //     updateTracking(getChildNode(node, p));
                return vProp;
            }
        }
        // Return an observable proxy to the property
        return getProxy(node, p);
    },
    // Forward all proxy properties to the target's value
    getPrototypeOf(node) {
        const value = getNodeValue(node);
        return value !== null && typeof value === 'object' ? Reflect.getPrototypeOf(value) : null;
    },
    ownKeys(node) {
        const value = getNodeValue(node);
        if (isPrimitive(value))
            return [];
        const keys = value ? Reflect.ownKeys(value) : [];
        // Update that this node was accessed for observers
        updateTracking(node, true);
        // This is required to fix this error:
        // TypeError: 'getOwnPropertyDescriptor' on proxy: trap reported non-configurability for
        // property 'length' which is either non-existent or configurable in the proxy node
        if (isArray(value) && keys[keys.length - 1] === 'length') {
            keys.splice(keys.length - 1, 1);
        }
        return keys;
    },
    getOwnPropertyDescriptor(node, p) {
        const value = getNodeValue(node);
        return !isPrimitive(value) ? Reflect.getOwnPropertyDescriptor(value, p) : undefined;
    },
    set(node, prop, value) {
        // If this assignment comes from within an observable function it's allowed
        if (inSet) {
            return Reflect.set(node, prop, value);
        }
        if (!inAssign) {
            return false;
        }
        setKey(node, prop, value);
        return true;
    },
    deleteProperty(target, prop) {
        // If this delete comes from within an observable function it's allowed
        if (inSet) {
            return Reflect.deleteProperty(target, prop);
        }
        else {
            return false;
        }
    },
    has(target, prop) {
        const value = getNodeValue(target);
        return Reflect.has(value, prop);
    },
};
function set(node, newValue) {
    if (!node.parent) {
        return setKey(node, symbolUndef, newValue);
    }
    else {
        return setKey(node.parent, node.key, newValue);
    }
}
function toggle(node) {
    const value = getNodeValue(node);
    if (isBoolean(value)) {
        set(node, !value);
        return !value;
    }
    else if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
        throw new Error('[legend-state] Cannot toggle a non-boolean value');
    }
}
function setKey(node, key, newValue, level) {
    if (process.env.NODE_ENV === 'development') {
        if (typeof HTMLElement !== 'undefined' && newValue instanceof HTMLElement) {
            console.warn(`[legend-state] Set an HTMLElement into state. You probably don't want to do that.`);
        }
    }
    if (node.root.locked) {
        throw new Error(process.env.NODE_ENV === 'development'
            ? '[legend-state] Cannot modify an observable while it is locked. Please make sure that you unlock the observable before making changes.'
            : '[legend-state] Modified locked observable');
    }
    const isDelete = newValue === symbolUndef;
    if (isDelete)
        newValue = undefined;
    const isRoot = key === symbolUndef;
    // Get the child node for updating and notifying
    let childNode = isRoot ? node : getChildNode(node, key);
    // Get the value of the parent
    let parentValue = isRoot ? node.root : ensureNodeValue(node);
    if (isRoot) {
        key = '_';
    }
    // Save the previous value first
    const prevValue = parentValue[key];
    // Compute newValue if newValue is a function or an observable
    newValue =
        !inAssign && isFunction(newValue)
            ? newValue(prevValue)
            : isObject(newValue) && (newValue === null || newValue === void 0 ? void 0 : newValue[symbolIsObservable])
                ? newValue.get()
                : newValue;
    const isPrim = isPrimitive(newValue) || newValue instanceof Date;
    inSet = true;
    // Save the new value
    if (isDelete) {
        delete parentValue[key];
    }
    else {
        parentValue[key] = newValue;
    }
    inSet = false;
    // Make sure we don't call too many listeners for ever property set
    beginBatch();
    let hasADiff = isPrim;
    let whenOptimizedOnlyIf = false;
    // If new value is an object or array update notify down the tree
    if (!isPrim || (prevValue && !isPrimitive(prevValue))) {
        hasADiff = updateNodes(childNode, newValue, prevValue);
        if (isArray(newValue)) {
            whenOptimizedOnlyIf = (newValue === null || newValue === void 0 ? void 0 : newValue.length) !== (prevValue === null || prevValue === void 0 ? void 0 : prevValue.length);
        }
    }
    if (NotifySpecifically.has(key)) {
        // Notify specifically at the child, not through children or parents
        doNotify(childNode, newValue, [], newValue, prevValue, 0);
    }
    else if (isPrim ? newValue !== prevValue : hasADiff) {
        // Notify for this element if something inside it has changed
        notify(isPrim && isRoot ? node : childNode, newValue, prevValue, (level !== null && level !== void 0 ? level : prevValue === undefined) ? -1 : hasADiff ? 0 : 1, whenOptimizedOnlyIf);
    }
    endBatch();
    return isRoot ? getProxy(node) : getProxy(node, key);
}
function assign(node, value) {
    const proxy = getProxy(node);
    beginBatch();
    if (isPrimitive(node.root._)) {
        node.root._ = {};
    }
    // Set inAssign to allow setting on safe observables
    inAssign = true;
    try {
        Object.assign(proxy, value);
    }
    finally {
        inAssign = false;
    }
    endBatch();
    return proxy;
}
function deleteFn(node, key) {
    // If called without a key, delete by key from the parent node
    if (key === undefined && isChildNodeValue(node)) {
        key = node.key;
        node = node.parent;
    }
    // delete sets to undefined first to notify
    setKey(node, key, symbolUndef, /*level*/ -1);
}
function createObservable(value, makePrimitive) {
    const valueIsPromise = isPromise(value);
    const root = {
        _: valueIsPromise ? undefined : value,
    };
    const node = {
        id: nextNodeID.current++,
        root,
    };
    const obs = makePrimitive || isActualPrimitive(value)
        ? new ObservablePrimitiveClass(node)
        : getProxy(node);
    if (valueIsPromise) {
        value.catch((error) => {
            obs.set({ error });
        });
        value.then((value) => {
            obs.set(value);
        });
    }
    return obs;
}
function observable(value) {
    return createObservable(value);
}
function observablePrimitive(value) {
    return createObservable(value, /*makePrimitive*/ true);
}

function setupTracking(nodes, update, noArgs) {
    let listeners = [];
    // Listen to tracked nodes
    if (nodes) {
        for (let tracked of nodes.values()) {
            const { node, track } = tracked;
            listeners.push(onChange(node, update, track, noArgs));
        }
    }
    return () => {
        if (listeners) {
            for (let i = 0; i < listeners.length; i++) {
                listeners[i]();
            }
            listeners = undefined;
        }
    };
}
function observe(selectorOrRun, reaction) {
    let dispose;
    const e = { num: 0 };
    // Wrap it in a function so it doesn't pass all the arguments to run()
    let update = function () {
        var _a;
        if (e.onCleanup) {
            e.onCleanup();
            e.onCleanup = undefined;
        }
        // Dispose listeners from previous run
        dispose === null || dispose === void 0 ? void 0 : dispose();
        // Run in a batch so changes don't happen until we're done tracking here
        beginBatch();
        // Begin tracking observables accessed while running
        beginTracking();
        // Run the function/selector
        delete e.value;
        const previous = e.previous;
        e.previous = computeSelector(selectorOrRun, e);
        if (!e.cancel) {
            const tracker = tracking.current;
            // Do tracing if it was requested
            let noArgs = true;
            if (tracker) {
                if (process.env.NODE_ENV === 'development' && tracker.nodes) {
                    (_a = tracker.traceListeners) === null || _a === void 0 ? void 0 : _a.call(tracker, tracker.nodes);
                    if (tracker.traceUpdates) {
                        noArgs = false;
                        update = tracker.traceUpdates(update);
                    }
                    // Clear tracing
                    tracker.traceListeners = undefined;
                    tracker.traceUpdates = undefined;
                }
                // Setup tracking with the nodes that were accessed
                dispose = setupTracking(tracker.nodes, update, noArgs);
            }
        }
        endTracking();
        if (e.onCleanupReaction) {
            e.onCleanupReaction();
            e.onCleanupReaction = undefined;
        }
        if (reaction && (e.num > 0 || !selectorOrRun[symbolIsEvent]) && previous !== e.previous) {
            e.value = e.previous;
            reaction(e);
        }
        // Increment the counter
        e.num++;
        endBatch();
    };
    update();
    // Return function calling dispose because dispose may be changed in update()
    return () => {
        var _a, _b;
        (_a = e.onCleanup) === null || _a === void 0 ? void 0 : _a.call(e);
        e.onCleanup = undefined;
        (_b = e.onCleanupReaction) === null || _b === void 0 ? void 0 : _b.call(e);
        e.onCleanupReaction = undefined;
        dispose();
    };
}

function computed(compute) {
    // Create an observable for this computed variable
    let obs = observable();
    lockObservable(obs, true);
    // Lazily activate the observable when get is called
    getNode(obs).root.activate = () => {
        const set = function (val) {
            // Update the computed value
            lockObservable(obs, false);
            obs.set(val);
            lockObservable(obs, true);
        };
        const fn = function () {
            let val = compute();
            if (isPromise(val)) {
                val.then((v) => set(v));
            }
            else {
                set(val);
            }
        };
        observe(fn);
    };
    return obs;
}

function event() {
    // event simply wraps around a number observable
    // which increments its value to dispatch change events
    const obs = observable(0);
    return {
        fire: function () {
            // Notify increments the value so that the observable changes
            obs.set((v) => v + 1);
        },
        on: function (cb) {
            return obs.onChange(cb);
        },
        get: () => obs.get(),
        // @ts-ignore
        [symbolIsObservable]: true,
        [symbolIsEvent]: true,
    };
}

function when(predicate, effect) {
    let value;
    // Create a wrapping fn that calls the effect if predicate returns true
    function run(e) {
        const ret = computeSelector(predicate);
        if (ret && !((isObject(ret) && isEmpty(ret)) || (isArray(ret) && ret.length === 0))) {
            value = ret;
            // If value is truthy then run the effect
            effect === null || effect === void 0 ? void 0 : effect(ret);
            // Set cancel so that observe does not track
            e.cancel = true;
        }
    }
    // Create an effect for the fn
    const dispose = observe(run);
    // If first run resulted in a truthy value just return it
    // It will have set e.cancel so no need to dispose
    if (value !== undefined) {
        return value;
    }
    else {
        // If no effect parameter return a promise
        const promise = !effect &&
            new Promise((resolve) => {
                effect = resolve;
            });
        return promise || dispose;
    }
}

exports.ObservablePrimitiveClass = ObservablePrimitiveClass;
exports.batch = batch;
exports.beginBatch = beginBatch;
exports.beginTracking = beginTracking;
exports.clone = clone;
exports.computeSelector = computeSelector;
exports.computed = computed;
exports.constructObject = constructObject;
exports.dateModifiedKey = dateModifiedKey;
exports.deconstructObject = deconstructObject;
exports.endBatch = endBatch;
exports.endTracking = endTracking;
exports.event = event;
exports.extraPrimitiveProps = extraPrimitiveProps;
exports.getNode = getNode;
exports.getNodeValue = getNodeValue;
exports.getObservableIndex = getObservableIndex;
exports.isArray = isArray;
exports.isBoolean = isBoolean;
exports.isEmpty = isEmpty;
exports.isFunction = isFunction;
exports.isObject = isObject;
exports.isObservable = isObservable;
exports.isPrimitive = isPrimitive;
exports.isPromise = isPromise;
exports.isString = isString;
exports.isSymbol = isSymbol;
exports.lockObservable = lockObservable;
exports.mergeIntoObservable = mergeIntoObservable;
exports.observable = observable;
exports.observablePrimitive = observablePrimitive;
exports.observe = observe;
exports.onChange = onChange;
exports.opaqueObject = opaqueObject;
exports.symbolDateModified = symbolDateModified;
exports.symbolDelete = symbolDelete;
exports.symbolIsEvent = symbolIsEvent;
exports.symbolIsObservable = symbolIsObservable;
exports.symbolUndef = symbolUndef;
exports.tracking = tracking;
exports.updateTracking = updateTracking;
exports.when = when;
//# sourceMappingURL=index.js.map
